import { useTenantSignup } from "@/api/useTenant"
import { Button, DatePicker, Form, Input, Rule, Title } from "@/atoms"
import { ALLOWED_SIGNUP_PARAMS } from "@/const/tenant-signup.constants"
import { getDate } from "@/helpers/date.helpers"
import { useTranslation } from "@/hooks/useTranslation"
import { useRecaptcha } from "@/pages/TenantSignupPage/hooks/useRecaptcha"

import styles from "./TenantSignupForm.module.scss"

import type { TenantSignupFormProps, ValuesTenantSignupForm } from "."

export const TenantSignupForm = (props: TenantSignupFormProps) => {
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const { t: tModal } = useTranslation("translation", {
    keyPrefix: "auth.tenantSignup"
  })
  const { searchParams, setIsTenantCreated } = props
  const { mutateAsync: signupMutateAsync, isPending: isSignupLoading } =
    useTenantSignup()
  const requiredRule: Rule = {
    required: true,
    message: t("error.requiredField")
  }
  const { reset, getToken, isRecaptchaLoaded } = useRecaptcha({
    containerId: "recaptcha-container",
    size: "normal",
    userAction: "signup"
  })

  const isComDomain = window.location.hostname.endsWith(".com")

  const setErrorMessage = (fieldName: string, error: string) => {
    form.setFields([
      {
        name: fieldName,
        errors: [error]
      }
    ])
  }

  const onFinish = async (values: ValuesTenantSignupForm) => {
    if (!isRecaptchaLoaded) {
      setErrorMessage("recaptcha", "Error loading recaptcha")
      return
    }
    const captchaValue = getToken()
    if (!captchaValue) {
      setErrorMessage("recaptcha", tModal("recaptchaError"))
      return
    }
    try {
      setErrorMessage("recaptcha", "")

      const filteredParams = Object.fromEntries(
        Array.from(searchParams.entries()).filter(([key]) =>
          ALLOWED_SIGNUP_PARAMS.includes(
            key as (typeof ALLOWED_SIGNUP_PARAMS)[number]
          )
        )
      )

      const payload: any = {
        tenant_name: values.tenantName,
        slug: values.tenantSlug,
        contact_name: values.contactName,
        contact_email: values.tenantEmail,
        re_token: captchaValue,
        ...filteredParams
      }

      // Add trial_end_date to payload if it exists (as milliseconds unix timestamp)
      if (values.trialEndDate) {
        payload.trial_end_date = values.trialEndDate.valueOf()
      }

      await signupMutateAsync(payload)

      setIsTenantCreated(true)
    } catch (error: any) {
      reset()
      const status = (error as Response).status
      switch (status) {
        case 403:
          setErrorMessage("submit", tModal("forbiddenMethodError"))
          break
        case 409:
          setErrorMessage("tenantSlug", tModal("slugAlreadyExists"))
          setErrorMessage("submit", "")
          break
        case 422:
          // Handle FastAPI validation errors
          if (error.detail && Array.isArray(error.detail)) {
            const errorMessages = error.detail
              .map((validationError: any) => {
                const fieldName = validationError.loc?.[1] || "unknown"
                const message = validationError.msg
                return `Field: ${fieldName}, Error: ${message}`
              })
              .join(" | ")
            setErrorMessage("submit", errorMessages)
          } else {
            setErrorMessage(
              "submit",
              "Validation error. Please check your input and try again."
            )
          }
          break
        default:
          setErrorMessage("submit", tModal("errorInCreatingOrganization"))
      }
    }
  }

  return (
    <Form
      form={form}
      validateTrigger={["onBlur"]}
      className={styles.form}
      name="tenantSignupForm"
      layout="vertical"
      requiredMark={true}
      onFinish={onFinish}
      scrollToFirstError={true}
    >
      <Title className={styles.title} level={4}>
        {tModal("title")}
      </Title>

      <Form.Item
        name="tenantName"
        label={tModal("organizationName")}
        rules={[
          requiredRule,
          { min: 4, message: tModal("organizationNameLengthMessage") },
          {
            pattern: new RegExp("^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$"),
            message: tModal("organizationNamePatternMessage")
          }
        ]}
      >
        <Input
          placeholder={tModal("organizationName") || ""}
          data-testid="tenantName"
        />
      </Form.Item>

      <Form.Item
        name="tenantSlug"
        label={tModal("organizationSlug")}
        rules={[
          requiredRule,
          { min: 4, message: tModal("organizationSlugLengthMessage") },
          {
            pattern: new RegExp("^[a-z0-9-]*$"),
            message: tModal("organizationSlugPatternMessage")
          }
        ]}
      >
        <Input
          placeholder={tModal("organizationSlug") || ""}
          data-testid="tenantSlug"
        />
      </Form.Item>

      <Form.Item
        name="contactName"
        label={tModal("contactName")}
        rules={[requiredRule]}
      >
        <Input
          placeholder={tModal("contactName") || ""}
          data-testid="contactName"
        />
      </Form.Item>

      <Form.Item
        name="tenantEmail"
        label={tModal("contactEmail")}
        rules={[
          requiredRule,
          { type: "email", message: t("error.emailInvalid") || "" }
        ]}
      >
        <Input
          placeholder={tModal("emailPlaceholder") || ""}
          data-testid="tenantEmail"
        />
      </Form.Item>

      {!isComDomain && (
        <Form.Item name="trialEndDate" label="Trial End Date">
          <DatePicker
            placeholder="Select trial end date"
            data-testid="trialEndDate"
            style={{ width: "100%" }}
            disabledDate={(current) => {
              // Disable dates before 7 days from now
              return (
                current && current < getDate().add(7, "days").startOf("day")
              )
            }}
          />
        </Form.Item>
      )}

      <Form.Item name="recaptcha">
        <div
          id="recaptcha-container"
          className={isRecaptchaLoaded ? "" : styles.error}
        >
          {isRecaptchaLoaded ? "" : tModal("recaptchaNotLoadedError")}
        </div>
      </Form.Item>
      <Form.Item name="submit">
        <Button
          block
          className={styles.button}
          disabled={isSignupLoading || !isRecaptchaLoaded}
          loading={isSignupLoading}
          htmlType="submit"
          size="large"
          type="primary"
        >
          {t("general.submit")}
        </Button>
      </Form.Item>
    </Form>
  )
}
